import { createFileRoute } from '@tanstack/react-router'

import LoadProgress from '@/components/LoadProgress'

import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'

import EditGrid, { useEditGrid } from '@/components/detail/EditGrid'
import useBasicGridColumns from '@/components/grid/BasicGridColumns'
import i18next, { t } from 'i18next'


import { defaultDynamicField, dynamicFieldSchema, DynamicFieldType } from '@/api/dynamicField'
import { organizationEnhancedDynamicFieldsQueryOptions, useCreateDynamicFieldMutation, useDeleteDynamicFieldMutation, useUpdateDynamicFieldMutation } from '@/api/dynamicFieldsQueryOptions'
import { JobType } from '@/api/jobs'
import { OrganizationLevelType } from '@/api/organizations'
import CreateDetailAccordion, { useCreateDetail } from '@/components/detail/CreateDetailRecord'
import AbbreviationColumn from '@/components/grid/AbbreviationColumn'
import NameColumn from '@/components/grid/NameColumn'
import useOrganizationDetailView from '@/components/hooks/useOrganizationDetailView'
import PermissionEditCell from '@/components/mui-x/PermissionEditCell'
import ScoregDatePicker from '@/components/mui/ScoregDatePicker'
import SelectDynamicFieldDataType from '@/components/select/SelectDynamicFieldDataType'
import SelectOrganizationLevel from '@/components/select/SelectOrganizationType'
import { getOrganization } from '@/store/auth/authSlice'
import { yupResolver } from '@hookform/resolvers/yup'
import { Grid } from '@mui/material'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'
import { useSelector } from 'react-redux'

export const Route = createFileRoute(
    '/_auth/organization_/$organizationUuid/dynamicFieldsTable',
)({
    component: DynamicFieldsTableComponent,
})

function DynamicFieldsTableComponent() {
    const params = Route.useParams()

    const { query, data, createable, updateMutation, deleteMutation, onSubmit } = useOrganizationDetailView<DynamicFieldType>(params, organizationEnhancedDynamicFieldsQueryOptions,
        'DynamicFields', useCreateDynamicFieldMutation, useUpdateDynamicFieldMutation, useDeleteDynamicFieldMutation)

    const sessionOrganization = useSelector(getOrganization)

    const basicGridColumns = useBasicGridColumns()
    const columns: GridColDef[] = []

    columns.push(
        AbbreviationColumn(20),
        NameColumn(100),
    )

    columns.push({
        field: 'datatype',
        headerName: t('property.dynamicField.datatype'),
        type: 'singleSelect',
        valueOptions: [
            { value: 'TEXT', label: t('enum.dynamicFieldDataType.TEXT') },
            { value: 'NUMBER', label: t('enum.dynamicFieldDataType.NUMBER') },
            { value: 'BOOL', label: t('enum.dynamicFieldDataType.BOOL') },
            { value: 'DATE', label: t('enum.dynamicFieldDataType.DATE') },
            { value: 'MEMBER', label: t('enum.dynamicFieldDataType.MEMBER') },
        ],
        editable: true,
    })
    columns.push({
        field: 'organizationLevel',
        headerName: t('property.dynamicField.organizationLevel'),
        type: 'singleSelect',
        valueOptions: sessionOrganization.organizationLevel == OrganizationLevelType.TROOP ? [
            { value: OrganizationLevelType.TROOP, label: t('enum.organizationLevel.TROOP') },
        ] : (sessionOrganization.organizationLevel == OrganizationLevelType.KOLONNE ? [
            { value: OrganizationLevelType.TROOP, label: t('enum.organizationLevel.TROOP') },
            { value: OrganizationLevelType.KOLONNE, label: t('enum.organizationLevel.KOLONNE') },
            { value: OrganizationLevelType.UNDEFINED, label: t('enum.organizationLevel.ALL') },
        ] : (sessionOrganization.organizationLevel == OrganizationLevelType.DIVISION ? [
            { value: OrganizationLevelType.TROOP, label: t('enum.organizationLevel.TROOP') },
            { value: OrganizationLevelType.KOLONNE, label: t('enum.organizationLevel.KOLONNE') },
            { value: OrganizationLevelType.DIVISION, label: t('enum.organizationLevel.DIVISION') },
            { value: OrganizationLevelType.UNDEFINED, label: t('enum.organizationLevel.ALL') },
        ] : [
            { value: OrganizationLevelType.TROOP, label: t('enum.organizationLevel.TROOP') },
            { value: OrganizationLevelType.KOLONNE, label: t('enum.organizationLevel.KOLONNE') },
            { value: OrganizationLevelType.DIVISION, label: t('enum.organizationLevel.DIVISION') },
            { value: OrganizationLevelType.FEDERATION, label: t('enum.organizationLevel.FEDERATION') },
            { value: OrganizationLevelType.UNDEFINED, label: t('enum.organizationLevel.ALL') },
        ])),
        editable: true,
    })

    columns.push(
        {
            field: 'readJobs',
            headerName: i18next.t('property.dynamicField.readJobs'),
            renderCell: (params) => {
                if (params.row.readJobs) {
                    return params.row.readJobs
                        .map((job: JobType) => job.abbreviation)
                        .join(', ')
                }
                return <></>
            },
            renderEditCell: (params: GridRenderCellParams) => {
                return <PermissionEditCell {...params} />
            },
            editable: true,
        }
    )

    columns.push(
        {
            field: 'writeJobs',
            headerName: i18next.t('property.dynamicField.writeJobs'),
            renderCell: (params) => {
                if (params.row.writeJobs) {
                    return params.row.writeJobs
                        .map((job: JobType) => job.abbreviation)
                        .join(', ')
                }
                return <></>
            },
            renderEditCell: (params: GridRenderCellParams) => {
                return <PermissionEditCell {...params} />
            },

            editable: true,
        }
    )

    columns.push(
        basicGridColumns.startDateColumn,
        basicGridColumns.endDateColumn,
    )


    const createDetail = useCreateDetail<DynamicFieldType>({
        onSubmit,
        resolver: yupResolver<DynamicFieldType, any, any>(dynamicFieldSchema),
        defaultValues: {
            ...defaultDynamicField,
            organization: {
                uuid: params.organizationUuid,
            },
        },
        title: t('action.new', { entity: t('entity.dynamicField') }),
    })

    const editGrid = useEditGrid<DynamicFieldType>({
        defaultValues: defaultDynamicField,
        updateMutation: updateMutation,
        deleteMutation: deleteMutation,
        records: data,
        columns: columns,
        title: t('entity.dynamicField'),
    })

    if (query.isLoading || query.isFetching) {
        return <LoadProgress />
    }

    return (
        <>
            <Typography>
                {t('entity.dynamicField')}
            </Typography>
            {createable && (
                <CreateDetailAccordion {...createDetail}>
                    <Grid container spacing={2}>
                        <input
                            type="hidden"
                            defaultValue={params.organizationUuid}
                            {...createDetail.form.register('organization.uuid')}
                        />
                        <input
                            type="hidden"
                            defaultValue={sessionOrganization.organizationLevel}
                            {...createDetail.form.register('organizationLevel')}
                        />
                        <Grid
                            size={{
                                xs: 12,
                                md: 3,
                                lg: 2
                            }}>
                            <TextField
                                defaultValue={defaultDynamicField.abbreviation}
                                required
                                {...createDetail.form.register('abbreviation')}
                                label={i18next.t('property.general.abbreviation')}
                                slotProps={{ htmlInput: { maxLength: 100 } }}
                                helperText={createDetail.form.formState.errors.abbreviation?.message}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <TextField
                                defaultValue={defaultDynamicField.name}
                                required
                                {...createDetail.form.register('name')}
                                label={i18next.t('property.general.name')}
                                slotProps={{ htmlInput: { maxLength: 100 } }}
                                helperText={createDetail.form.formState.errors.name?.message}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <SelectDynamicFieldDataType control={createDetail.form.control} name="datatype" />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <SelectOrganizationLevel control={createDetail.form.control} name="organizationLevel" limitDown={true} includeUndefined />
                            <Typography variant="caption">{i18next.t('property.dynamicField.organizationLevelDescription')}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <ScoregDatePicker
                                name="startDate"
                                defaultValue={dayjs(defaultDynamicField.startDate)}
                                disabled={false}
                                control={createDetail.form.control}
                                label={t('property.general.startDate')}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <ScoregDatePicker
                                name="endDate"
                                defaultValue={null}
                                disabled={false}
                                control={createDetail.form.control}
                                label={t('property.general.endDate')}
                            />
                        </Grid>
                    </Grid>
                </CreateDetailAccordion>
            )}
            <EditGrid {...editGrid}></EditGrid>
        </>
    )
}
